import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Color, GameMode, MovementDirection } from "../Types";

const LS_KEY = "nardy_settings";

interface PersistedSettings {
  autoDice: boolean;
  soundEnabled: boolean;
  soundVolume: number;
}

function loadPersisted(): PersistedSettings {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return { autoDice: false, soundEnabled: true, soundVolume: 0.6 };
}

function savePersisted(s: PersistedSettings) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(s)); } catch (_) {}
}

const persisted = loadPersisted();

interface SettingsState {
  movementDirection: MovementDirection;
  playerOneColor: Color;
  showGameOverDialog: boolean;
  showSettingsMenu: boolean;
  showMatchSetupScreen: boolean;
  autoDice: boolean;
  soundEnabled: boolean;
  soundVolume: number;
  gameMode: GameMode;
}

const initialState: SettingsState = {
  movementDirection:  MovementDirection.CounterClockwise,
  playerOneColor:     Color.White,
  showGameOverDialog: false,
  showSettingsMenu:   false,
  showMatchSetupScreen: false,
  autoDice:     persisted.autoDice,
  soundEnabled: persisted.soundEnabled,
  soundVolume:  persisted.soundVolume,
  gameMode:     GameMode.Backgammon,
};

export const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setMovementDirection: (state, action: PayloadAction<MovementDirection>) => {
      state.movementDirection = action.payload;
    },
    setPlayerOneColor: (state, action: PayloadAction<Color>) => {
      state.playerOneColor = action.payload;
    },
    setShowGameOverDialog: (state, action: PayloadAction<boolean>) => {
      state.showGameOverDialog = action.payload;
    },
    setShowSettingsMenu: (state, action: PayloadAction<boolean>) => {
      state.showSettingsMenu = action.payload;
    },
    setShowMatchSetupScreen: (state, action: PayloadAction<boolean>) => {
      state.showMatchSetupScreen = action.payload;
    },
    setAutoDice: (state, action: PayloadAction<boolean>) => {
      state.autoDice = action.payload;
      savePersisted({ autoDice: action.payload, soundEnabled: state.soundEnabled, soundVolume: state.soundVolume });
    },
    setSoundEnabled: (state, action: PayloadAction<boolean>) => {
      state.soundEnabled = action.payload;
      savePersisted({ autoDice: state.autoDice, soundEnabled: action.payload, soundVolume: state.soundVolume });
    },
    setSoundVolume: (state, action: PayloadAction<number>) => {
      state.soundVolume = action.payload;
      savePersisted({ autoDice: state.autoDice, soundEnabled: state.soundEnabled, soundVolume: action.payload });
    },
    setGameMode: (state, action: PayloadAction<GameMode>) => {
      state.gameMode = action.payload;
    },
  },
});

export const {
  setMovementDirection,
  setPlayerOneColor,
  setShowGameOverDialog,
  setShowSettingsMenu,
  setShowMatchSetupScreen,
  setAutoDice,
  setSoundEnabled,
  setSoundVolume,
  setGameMode,
} = settingsSlice.actions;

export default settingsSlice.reducer;
